import React, { useEffect, useState } from "react";
import { useForm } from "../useForm";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import DatePickerInput from "../../controls/DatePickerInput";
import Input from "../../controls/Input";
import ImportMediaDropzone from "../../importMedia/ImportMediaDropzone";

const initialFValues = {
  description: "",
  equipment_name: "",
  equipment_barcode: "",
  required_maintenance_date: null,
  image1: "",
  image2: "",
};

export default function ProductRndEquipmentForm(props) {
  const { equipment, description, setOpen, actionCall, isEdit } = props;

  const [image1Preview, setImage1Preview] = useState(null);
  const [image2Preview, setImage2Preview] = useState(null);

  const { values, setValues, handleInputChange } = useForm(
    initialFValues,
    false
  );

  useEffect(() => {
    if (isEdit && equipment) {
      setValues({
        description: equipment.description || "",
        equipment_name: equipment.equipment_name || "",
        equipment_barcode: equipment.equipment_barcode || "",
        required_maintenance_date: equipment.required_maintenance_date || null,
        image1: equipment.image1 || "",
        image2: equipment.image2 || "",
      });
    } else if (description) {
      setValues((v) => ({ ...v, description: description }));
    }
  }, [isEdit, equipment, description, setValues]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Prepare payload - convert null images to string "null" for backend deletion
    const payload = { ...values };

    if (payload.image1 === null) {
      payload.image1 = "null";
    }

    if (payload.image2 === null) {
      payload.image2 = "null";
    }

    if (isEdit && equipment) {
      actionCall(equipment, payload);
    } else {
      actionCall(payload);
    }
  };

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
        <div className="px-4 sm:px-6">
          <div className="flex items-start justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              {isEdit
                ? `Edit Equipment: ${equipment?.equipment_name || ""}`
                : "Add Equipment"}
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={() => setOpen({ childComponent: null, open: false })}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        <div className="relative mt-6 flex-1 px-4 sm:px-6">
          <div>
            <div className="mt-2">
              <Input
                label="Equipment Name"
                name="equipment_name"
                onChange={handleInputChange}
                value={values.equipment_name}
                labelOn={true}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
              <div className="mt-2">
                <Input
                  label="Description"
                  name="description"
                  onChange={handleInputChange}
                  value={values.description}
                  labelOn={true}
                  type="text"
                  className="mt-4 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>
              <div className="mt-2">
                <Input
                  label="Equipment Barcode"
                  name="equipment_barcode"
                  onChange={handleInputChange}
                  value={values.equipment_barcode}
                  labelOn={true}
                  type="text"
                  className="mt-4 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>

              <div className="mt-2">
                <DatePickerInput
                  label="Required Maintenance Date"
                  name="required_maintenance_date"
                  onChange={handleInputChange}
                  value={values.required_maintenance_date}
                  labelOn={true}
                  type="text"
                  dateFormat={"LLL dd, YYY"}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>

              {/* Image Upload Section */}
              <div className="mt-6 border-t pt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">
                  Equipment Images
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Image 1 */}
                  <div>
                    <div className="flex space-x-2 mb-2">
                      <label
                        htmlFor="image1"
                        className="block text-sm font-medium text-gray-900"
                      >
                        Image 1
                      </label>
                    </div>
                    {image1Preview ? (
                      <div className="mb-2">
                        <img
                          src={image1Preview}
                          height="140"
                          width="140"
                          alt="Image 1 preview"
                          className="rounded-md object-cover"
                        />
                      </div>
                    ) : values.image1 ? (
                      <div className="mb-2">
                        <img
                          src={values.image1}
                          alt="Equipment image 1"
                          title="Equipment image 1"
                          className="media-image rounded-md object-cover"
                          height="140"
                          width="140"
                        />
                      </div>
                    ) : null}
                    <div className="flex gap-2">
                      <ImportMediaDropzone
                        acceptedType={{ "image/*": [".png", ".jpg", ".jpeg"] }}
                        fileName="image1"
                        handleInputChange={handleInputChange}
                        setUploadedImage={setImage1Preview}
                      />
                      {(values.image1 || image1Preview) && (
                        <button
                          type="button"
                          className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                          onClick={() => {
                            setValues({ ...values, image1: null });
                            setImage1Preview(null);
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Image 2 */}
                  <div>
                    <div className="flex space-x-2 mb-2">
                      <label
                        htmlFor="image2"
                        className="block text-sm font-medium text-gray-900"
                      >
                        Image 2
                      </label>
                    </div>
                    {image2Preview ? (
                      <div className="mb-2">
                        <img
                          src={image2Preview}
                          height="140"
                          width="140"
                          alt="Image 2 preview"
                          className="rounded-md object-cover"
                        />
                      </div>
                    ) : values.image2 ? (
                      <div className="mb-2">
                        <img
                          src={values.image2}
                          alt="Equipment image 2"
                          title="Equipment image 2"
                          className="media-image rounded-md object-cover"
                          height="140"
                          width="140"
                        />
                      </div>
                    ) : null}
                    <div className="flex gap-2">
                      <ImportMediaDropzone
                        acceptedType={{ "image/*": [".png", ".jpg", ".jpeg"] }}
                        fileName="image2"
                        handleInputChange={handleInputChange}
                        setUploadedImage={setImage2Preview}
                      />
                      {(values.image2 || image2Preview) && (
                        <button
                          type="button"
                          className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                          onClick={() => {
                            setValues({ ...values, image2: null });
                            setImage2Preview(null);
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
          onClick={() => setOpen({ childComponent: null, open: false })}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          onClick={handleSubmit}
        >
          {isEdit ? "Save" : "Create"}
        </button>
      </div>
    </div>
  );
}
